import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { IPagination } from '@simOn/common/http';
import { UserInfoCard } from '@simOn/common/user-info-card-models';
import {
  AddTaskComment,
  CreateTaskTicket,
  EditTaskComment,
  TaskActivity,
  TaskChecklistItemCheck,
  TaskCommentActivity,
  TaskListEntry,
  TaskListFilteringOptions,
  TaskListFilters,
  TaskListFiltersWithPage,
  TaskListPaginated,
  TaskTicketDetail,
  TaskTicketStatusUpdate,
  UpdateTaskTicket
} from '@simOn/ticket/element/models';
import { API_URL } from '@simOn/utils/tokens';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TasksListApiService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  getTicketsPaginated(filters: Partial<TaskListFiltersWithPage>): Observable<TaskListPaginated> {
    const body = {
      pageFilter: {
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize
      },
      ticketFilter: this._toTicketFilter(filters),
      ticketSort: {
        sortBy: filters.sortBy,
        isAscending: filters.isAscending
      }
    };
    return this._http.post<IPagination<TaskListEntry>>(`${this._apiUrl}/Tickets/GetTicketsPaginated`, body);
  }

  getFilteringOptions(filters: Partial<TaskListFilters>): Observable<TaskListFilteringOptions> {
    return this._http.post<TaskListFilteringOptions>(
      `${this._apiUrl}/Tickets/GetFilteringOptions`,
      this._toTicketFilter(filters)
    );
  }

  createTicket(payload: CreateTaskTicket, apartmentId: string): Observable<string> {
    return this._http.post<string>(`${this._apiUrl}/Tickets/CreateTicket`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  getTicket(ticketId: string, apartmentId: string): Observable<TaskTicketDetail> {
    return this._http.get<TaskTicketDetail>(`${this._apiUrl}/Tickets/GetTicket?TicketId=${ticketId}`, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  updateTicket(payload: UpdateTaskTicket, apartmentId: string): Observable<void> {
    return this._http.put<void>(`${this._apiUrl}/Tickets/UpdateTicket`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  deleteTicket(ticketId: string): Observable<void> {
    return this._http.delete<void>(`${this._apiUrl}/Tickets/DeleteTicket?TicketId=${ticketId}`);
  }

  updateTicketStatus(payload: TaskTicketStatusUpdate, apartmentId: string): Observable<void> {
    return this._http.put<void>(`${this._apiUrl}/Tickets/UpdateTicketStatus`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  checkChecklistItem(payload: TaskChecklistItemCheck, apartmentId: string): Observable<void> {
    return this._http.put<void>(`${this._apiUrl}/Tickets/CheckTicketChecklistItem`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  getTicketActivities(ticketId: string, apartmentId: string): Observable<TaskActivity[]> {
    return this._http.get<TaskActivity[]>(`${this._apiUrl}/Tickets/GetTicketActivities?TicketId=${ticketId}`, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  addTicketComment(payload: AddTaskComment, apartmentId: string): Observable<TaskCommentActivity> {
    return this._http.post<TaskCommentActivity>(`${this._apiUrl}/Tickets/AddTicketComment`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  editTicketComment(payload: EditTaskComment, apartmentId: string): Observable<TaskCommentActivity> {
    return this._http.put<TaskCommentActivity>(`${this._apiUrl}/Tickets/EditTicketComment`, payload, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  softDeleteTicketComment(ticketCommentId: string, apartmentId: string): Observable<void> {
    return this._http.delete<void>(`${this._apiUrl}/Tickets/DeleteTicketComment?TicketCommentId=${ticketCommentId}`, {
      headers: new HttpHeaders({ apartmentId })
    });
  }

  hardDeleteTicketComment(ticketCommentId: string, apartmentId: string): Observable<void> {
    return this._http.delete<void>(
      `${this._apiUrl}/Tickets/HardDeleteTicketComment?TicketCommentId=${ticketCommentId}`,
      { headers: new HttpHeaders({ apartmentId }) }
    );
  }

  getUsersAssignableToTicket(ticketId?: string): Observable<UserInfoCard[]> {
    const query = ticketId ? `?TicketId=${ticketId}` : '';
    return this._http.get<UserInfoCard[]>(`${this._apiUrl}/Tickets/GetUsersAssignableToTicket${query}`);
  }

  private _toTicketFilter(filters: Partial<TaskListFilters>): Partial<TaskListFilters> {
    return {
      name: filters.name,
      onlyUnread: filters.onlyUnread,
      statuses: filters.statuses,
      authorIds: filters.authorIds,
      stakeholderIds: filters.stakeholderIds,
      dueDateFrom: filters.dueDateFrom,
      dueDateTo: filters.dueDateTo,
      editedAtFrom: filters.editedAtFrom,
      editedAtTo: filters.editedAtTo,
      assignedOnly: filters.assignedOnly,
      taskTypes: filters.taskTypes,
      apartmentIds: filters.apartmentIds,
      roomIds: filters.roomIds
    };
  }
}
